export const FRAME_AUTH_APP = 'gamefe';
export const ACCESS_COOKIE_NAME = 'APP_AUTH_ACCESS';
export const HELLO_TIMEOUT_MS = 3000;
export const TOKEN_WAIT_MS = 10000;

const TRUSTED_HOST_RE = /(^|\.)onclass\.(com|me|tech|space|local)$/i;

export interface AuthHelloMessage {
  type: 'auth:hello';
  app: typeof FRAME_AUTH_APP;
}

export interface AuthExpiredMessage {
  type: 'auth:expired';
  app: typeof FRAME_AUTH_APP;
}

export interface AuthTokenMessage {
  type: 'auth:token';
  access: string;
  expiresAt?: number;
  userId?: string;
  lang?: string;
}

export interface AuthLogoutMessage {
  type: 'auth:logout';
}

export function isIframeWindow(win: Window = window): boolean {
  return win.parent !== win;
}

export function isTrustedFrameOrigin(origin: string): boolean {
  if (!origin) {
    return false;
  }
  try {
    const { hostname } = new URL(origin);
    return TRUSTED_HOST_RE.test(hostname);
  } catch {
    return false;
  }
}

export function apiHostToPortalHost(host: string): string {
  const hostname = host
    .replace(/^https?:\/\//i, '')
    .split('/')[0]
    .split(':')[0];
  return hostname.replace(/^b\./i, '');
}

export function hostToOrigin(hostname: string): string {
  if (/^https?:\/\//i.test(hostname)) {
    return new URL(hostname).origin;
  }
  const protocol =
    /\.local$/i.test(hostname) || hostname === 'localhost' ? 'http:' : 'https:';
  return `${protocol}//${hostname}`;
}

export function resolveParentOrigin(input: {
  referrer?: string;
  ancestorOrigin?: string | null;
  queryHost?: string;
}): string | null {
  const fromReferrer = trustedOriginFromUrl(input.referrer);
  if (fromReferrer) {
    return fromReferrer;
  }

  if (input.ancestorOrigin && isTrustedFrameOrigin(input.ancestorOrigin)) {
    return originOf(input.ancestorOrigin);
  }

  if (input.queryHost) {
    const origin = hostToOrigin(apiHostToPortalHost(input.queryHost));
    if (isTrustedFrameOrigin(origin)) {
      return origin;
    }
  }

  return null;
}

export function currentAncestorOrigin(): string | null {
  try {
    const origins = window.location.ancestorOrigins;
    return origins && origins.length > 0 ? origins[0] : null;
  } catch {
    return null;
  }
}

export function readAccessCookie(
  cookieSource: string = document.cookie
): string | null {
  const escaped = ACCESS_COOKIE_NAME.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&');
  const match = cookieSource.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  if (!match) {
    return null;
  }
  try {
    const value = decodeURIComponent(match[1]).trim();
    return value || null;
  } catch {
    return match[1] || null;
  }
}

export function isTrustedApiUrl(url: string, apiHost: string): boolean {
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin === window.location.origin) {
      return false;
    }
    if (apiHost) {
      const normalized = apiHost
        .replace(/^https?:\/\//i, '')
        .split('/')[0]
        .split(':')[0];
      if (parsed.hostname === normalized) {
        return true;
      }
    }
    return isTrustedFrameOrigin(parsed.origin);
  } catch {
    return false;
  }
}

function trustedOriginFromUrl(urlOrOrigin?: string): string | null {
  if (!urlOrOrigin) {
    return null;
  }
  try {
    const origin = new URL(urlOrOrigin, window.location.href).origin;
    return isTrustedFrameOrigin(origin) ? origin : null;
  } catch {
    return null;
  }
}

function originOf(originOrUrl: string): string {
  try {
    return new URL(originOrUrl).origin;
  } catch {
    return originOrUrl;
  }
}
